import { type CodeFile, type EditorWidget, type Theme } from 'mini-code'
import type React from 'react'
import {
  type NumberWithParamsInfo,
} from '../bytecode.ts'
import { DragState, Handlers, updateValueWithSpacing } from './common.ts'

export class SliderWidget {
  private sliderKey: string
  private currentWidth: number
  private sliderHeight = 20
  private padding = 0
  private handleRadius = 7

  constructor(
    private info: NumberWithParamsInfo,
    private theme: Theme,
    private codeFileRef: React.RefObject<CodeFile>,
    private dragStateRef: React.RefObject<DragState | null>,
    private handlersRef: React.RefObject<Handlers>,
    private rafRef: React.RefObject<number | null>,
    private containerRef: React.RefObject<HTMLDivElement | null>,
    private triggerRender: (fn: (n: number) => number) => void,
  ) {
    this.sliderKey = `${info.line}-${info.column}`
    this.currentWidth = info.length * 8
  }

  toEditorWidget(): EditorWidget {
    return {
      type: 'below',
      line: this.info.line,
      column: this.info.column,
      length: this.info.length,
      height: this.sliderHeight,

      render: (ctx, x, y, width, height) => {
        y -= 2
        this.currentWidth = width
        const drag = this.dragStateRef.current

        // Check for active drag FIRST before using info.value
        let value: number
        if (drag && drag.isDragging && drag.line === this.info.line && drag.column === this.info.column) {
          // ALWAYS use drag value during active drag
          value = drag.value
          // Update position info for current widget instance
          drag.x = x
          drag.y = y
          drag.width = width
          drag.min = this.info.min
          drag.max = this.info.max
          drag.key = this.sliderKey
        }
        else {
          // No active drag, use info value
          value = this.info.value
        }

        // Clamp value to range to prevent render errors
        value = Math.max(this.info.min, Math.min(this.info.max, value))
        const range = this.info.max - this.info.min
        const normalized = Math.max(0, Math.min(1, (value - this.info.min) / range))

        ctx.fillStyle = this.theme.colors.parameter || '#666'
        ctx.fillRect(x + this.padding, y + height / 2 - 1, width - this.padding * 2, 2)

        const trackWidth = width - this.padding * 2
        const handleX = x + this.padding + this.handleRadius + normalized * (trackWidth - 2 * this.handleRadius)
        const handleY = y + height / 2

        ctx.fillStyle = this.theme.background === 'transparent' ? '#000' : this.theme.background
        ctx.beginPath()
        ctx.arc(handleX, handleY, this.handleRadius, 0, Math.PI * 2)
        ctx.fill()

        ctx.strokeStyle = this.theme.colors.parameter || '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
      },

      pointerDown: (x, y, offsetX) => {
        const trackWidth = this.currentWidth - this.padding * 2
        const range = this.info.max - this.info.min
        const normalized = Math.max(0,
          Math.min(1, (offsetX - this.padding - this.handleRadius) / (trackWidth - 2 * this.handleRadius)))
        const value = Math.max(this.info.min, Math.min(this.info.max, this.info.min + normalized * range))

        this.dragStateRef.current = {
          key: this.sliderKey,
          value,
          min: this.info.min,
          max: this.info.max,
          line: this.info.line,
          column: this.info.column,
          length: this.info.length,
          x,
          y,
          width: this.currentWidth,
          isDragging: true,
        }

        // Mark that we're dragging
        this.handlersRef.current = { move: true as any, up: true as any }

        // Update the code with initial value
        const lines = this.codeFileRef.current.value.split('\n')
        const lineIndex = this.info.line - 1
        if (lineIndex >= 0 && lineIndex < lines.length) {
          const line = lines[lineIndex]
          const match = line.substring(this.info.column - 1).match(/^-?\d*\.?\d*/)
          const oldStr = match ? match[0] : String(this.info.value)
          lines[lineIndex] = updateValueWithSpacing(line, this.info.column, oldStr, value, this.info.length)
          this.codeFileRef.current.value = lines.join('\n')
        }
      },

      pointerMove: (x, y, offsetX, offsetY) => {
        const drag = this.dragStateRef.current
        if (!drag || !drag.isDragging) return
        if (drag.line !== this.info.line || drag.column !== this.info.column) return

        const trackWidth = drag.width - this.padding * 2
        const normalized = Math.max(0,
          Math.min(1, (offsetX - this.padding - this.handleRadius) / (trackWidth - 2 * this.handleRadius)))
        drag.value = Math.max(drag.min, Math.min(drag.max, drag.min + normalized * (drag.max - drag.min)))

        if (this.rafRef.current) cancelAnimationFrame(this.rafRef.current)
        this.rafRef.current = requestAnimationFrame(() => {
          const currentDrag = this.dragStateRef.current
          if (!currentDrag) {
            this.rafRef.current = null
            return
          }
          const lines = this.codeFileRef.current.value.split('\n')
          const lineIndex = currentDrag.line - 1
          if (lineIndex >= 0 && lineIndex < lines.length) {
            const line = lines[lineIndex]
            const match = line.substring(currentDrag.column - 1).match(/^-?\d*\.?\d*/)
            const oldStr = match ? match[0] : ''
            lines[lineIndex] = updateValueWithSpacing(line, currentDrag.column, oldStr, currentDrag.value,
              currentDrag.length)
            this.codeFileRef.current.value = lines.join('\n')
          }
          this.triggerRender(n => n + 1)
          this.rafRef.current = null
        })
      },

      pointerUp: () => {
        this.handlersRef.current = { move: null, up: null }
        if (this.rafRef.current) {
          cancelAnimationFrame(this.rafRef.current)
          this.rafRef.current = null
        }
      },
    }
  }
}
